import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  moveContacts,
  moveContactsUserPortal,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Biography, ContactGroup, Person } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import { GridColDef } from '@mui/x-data-grid-pro';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { FormPerson } from './edit-personal-contact';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface MoveContactsProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  userView?: boolean;
  contactList: Person[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  selectedLabel: string;
  selectedRows: string[];
  labels: ContactGroup[];
  changeLogRoles: string[];
}

export default function MoveContactsDialog({
  open,
  handleClose,
  setSelectedRows,
  userEmail,
  userView,
  contactList,
  selectedLabel,
  selectedRows,
  labels,
  changeLogRoles,
}: MoveContactsProps) {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { mutate, isPending } = useMutation({
    mutationFn: (data: FormPerson[]) =>
      moveContacts(userEmail, data, selectedLabels, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Contacts moved successfully!',
      });
      selectedLabels.forEach((label) => {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.ContactGroup, userEmail, label],
        });
        queryClient.invalidateQueries({
          queryKey: [UserKeys.Contacts, userEmail, label],
        });
      });
      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue moving these contacts. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.personal_contacts
          .move_personal_contacts,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.personalContacts,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: (data: FormPerson[]) =>
        moveContactsUserPortal(data, selectedLabels),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Contacts moved successfully!',
        });
        selectedLabels.forEach((label) => {
          queryClient.invalidateQueries({
            queryKey: [UserKeys.ContactGroup, userEmail, label],
          });
          queryClient.invalidateQueries({
            queryKey: [UserKeys.Contacts, userEmail, label],
          });
        });
        setSelectedRows([]);
        setChangeLogMessage('');
        handleClose();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue moving these contacts. Please refresh and try again.',
        });
      },
    });

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Label',
      editable: false,
      sortable: true,
      filterable: true,
      flex: 1,
    },
  ];

  function handleSubmit() {
    if (
      !userView &&
      isAbleToSeeUIElement(changeLogRoles, UserRoles.MoveContacts.value)
    ) {
      setOpenChangeLogDialog(true);
    } else if (userView) {
      const updatedContacts = selectedContacts.map((contact) => {
        const updatedContact = {
          ...contact,
          biographies: {
            value: contact.biographies ? contact.biographies[0].value : '',
          } as Biography,
        };
        return updatedContact;
      });
      userPortalMutate(updatedContacts);
    } else {
      const updatedContacts = selectedContacts.map((contact) => {
        const updatedContact = {
          ...contact,
          biographies: {
            value: contact.biographies ? contact.biographies[0].value : '',
          } as Biography,
        };
        return updatedContact;
      });
      mutate(updatedContacts);
    }
  }

  const labelsData: {
    id: string;
    email: string;
  }[] = [];

  labels?.forEach((label) => {
    if (
      label.resourceName !== 'contactGroups/blocked' &&
      label.resourceName !== 'contactGroups/chatBuddies'
    ) {
      // Unique ID of every Label
      const id = label.resourceName || '';
      // Email
      const email = label.formattedName;
      // Final Grid Data
      labelsData.push({
        id,
        email,
      });
    }
  });

  const selectedContacts = contactList.filter((group) =>
    selectedRows.includes(group.resourceName || '')
  );

  let contactName;
  if (selectedContacts.length === 1) {
    contactName =
      selectedContacts[0].names &&
      selectedContacts[0].names[0] &&
      selectedContacts[0].names[0].displayName
        ? selectedContacts[0].names[0].displayName
        : selectedContacts[0].emailAddresses &&
          selectedContacts[0].emailAddresses[0] &&
          selectedContacts[0].emailAddresses[0].value
        ? selectedContacts[0].emailAddresses[0].value
        : selectedContacts[0].resourceName;
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setSelectedLabels([]);
      }}
      title={selectedContacts.length > 1 ? 'Move Contacts' : 'Move Contact'}
      dialogContent={
        <Box>
          <Typography sx={{ mb: 2 }}>
            {selectedContacts.length > 1 ? (
              <>
                Move <strong>{selectedContacts.length}</strong> contacts to
                selected label(s).
              </>
            ) : (
              <>
                Move <strong>{contactName}</strong> to selected label(s).
              </>
            )}
          </Typography>
          <Box sx={{ height: 350 }}>
            <SharedDataGridClient
              showToolbar={false}
              disableColumnMenu
              checkboxSelection
              rows={labelsData.filter((label) => label.id !== selectedLabel)}
              columns={columns}
              defaultGridState={{
                sorting: {
                  sortModel: [{ field: 'email', sort: 'asc' }],
                },
              }}
              selectedRows={selectedLabels}
              setSelectedRows={setSelectedLabels}
            />
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const updatedContacts = selectedContacts.map((contact) => {
                const updatedContact = {
                  ...contact,
                  biographies: {
                    value: contact.biographies
                      ? contact.biographies[0].value
                      : '',
                  } as Biography,
                };
                return updatedContact;
              });
              mutate(updatedContacts);
            }}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: selectedContacts.length > 1 ? 'Move Contacts' : 'Move Contact',
        action: handleSubmit,
        loading: isPending || userPortalPending,
        disabled: selectedLabels.length === 0,
      }}
    />
  );
}
